/**
 * Shared commission plan tier display components.
 *
 * Used by the Commission Earner popup modal and the Recruiter detail page.
 * The tier ladder table matches the Admin plan card format (From / To / schedule columns).
 */

import { money } from '../format';

// ── Shared types ──────────────────────────────────────────────────────

export interface TierDisplay {
  readonly threshold: string;
  readonly rate: string;
}

export interface ScheduleRates {
  readonly name: string;
  readonly isElevated: boolean;
  /** Rates in bracket order (same length as tiers array). */
  readonly rates: readonly string[];
}

export interface PlanTierCardProps {
  /** Sorted tier brackets (lowest threshold first). */
  readonly tiers: readonly TierDisplay[];
  readonly tierMode: string;
  /** Earner's credited GP — used to highlight the active tier. */
  readonly currentGp: number;
  /** Plan display name (e.g., "Sales Commission"). */
  readonly planName?: string | null;
  /** Elevated window duration in months (shown in subtitle). */
  readonly elevatedPeriodMonths?: number | null;
  /**
   * Per-schedule rates for the full ladder table.
   * When provided, the table shows one column per schedule (Elevated, Standard).
   * When omitted, a single "Rate" column is shown from the `tiers` rate field.
   */
  readonly schedules?: readonly ScheduleRates[];
}

// ── Helpers ───────────────────────────────────────────────────────────

/** Find the highest tier bracket the earner has reached. */
export function findCurrentTier(tiers: readonly TierDisplay[], gp: number): TierDisplay | null {
  let current: TierDisplay | null = null;
  for (const tier of tiers) {
    if (gp >= Number(tier.threshold)) current = tier;
  }
  return current;
}

/**
 * Derive display-friendly From/To ranges from sorted bracket thresholds.
 * Reuses the same logic as Admin.tsx's `bracketRanges()`.
 */
function bracketRanges(tiers: readonly TierDisplay[]): { fromLabel: string; toLabel: string }[] {
  return tiers.map((t, i) => {
    const from = Number(t.threshold);
    const next = tiers[i + 1];
    const to = next ? Number(next.threshold) - 1 : null;
    return {
      fromLabel: `$${from.toLocaleString()}`,
      toLabel: to !== null ? `$${to.toLocaleString()}` : '\u2014',
    };
  });
}

// ── TierGauge — visual progress bar ───────────────────────────────────

export function TierGauge({
  tiers,
  currentGp,
}: {
  tiers: readonly TierDisplay[];
  currentGp: number;
}) {
  if (tiers.length === 0) return null;

  const maxThreshold = Math.max(...tiers.map((t) => Number(t.threshold)), currentGp * 1.1);
  const scale = maxThreshold > 0 ? 100 / maxThreshold : 0;
  const positionPct = Math.min(currentGp * scale, 100);

  return (
    <div>
      <div className="relative h-8 rounded-full bg-surface-overlay overflow-hidden">
        {tiers.map((tier, i) => {
          const start = Number(tier.threshold) * scale;
          const nextThreshold =
            i + 1 < tiers.length ? Number(tiers[i + 1]?.threshold ?? 0) * scale : 100;
          const width = nextThreshold - start;
          const opacity = 0.15 + (i / (tiers.length - 1 || 1)) * 0.35;
          return (
            <div
              key={tier.threshold}
              className="absolute inset-y-0"
              style={{
                left: `${start}%`,
                width: `${width}%`,
                backgroundColor: `rgba(22, 163, 74, ${opacity})`,
              }}
              title={`${(Number(tier.rate) * 100).toFixed(0)}% from ${money(tier.threshold)}`}
            />
          );
        })}
        <div
          className="absolute inset-y-0 w-0.5 bg-ink"
          style={{ left: `${positionPct}%` }}
          title={`Current: ${money(String(currentGp.toFixed(4)))}`}
        />
      </div>
      <div className="mt-3 flex flex-wrap gap-4">
        {tiers.map((tier) => {
          const active = currentGp >= Number(tier.threshold);
          return (
            <div key={tier.threshold} className="text-center">
              <p
                className={`figure text-sm font-semibold ${active ? 'text-profit' : 'text-ink-faint'}`}
              >
                {(Number(tier.rate) * 100).toFixed(0)}%
              </p>
              <p className="text-[11px] text-ink-faint">
                {Number(tier.threshold) === 0 ? 'Base' : `≥ ${money(tier.threshold)}`}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── PlanTierCard — full plan display with ladder table ─────────────────

export function PlanTierCard({
  tiers,
  tierMode,
  currentGp,
  planName,
  elevatedPeriodMonths,
  schedules,
}: PlanTierCardProps) {
  if (tiers.length === 0) {
    return <p className="text-sm text-ink-faint">No commission tiers configured.</p>;
  }

  const currentTier = findCurrentTier(tiers, currentGp);
  const ranges = bracketRanges(tiers);
  const hasSchedules = schedules !== undefined && schedules.length > 0;

  return (
    <div className="space-y-4">
      {/* Header: plan name + mode */}
      <div>
        <div className="flex items-center gap-2">
          {planName && <h3 className="text-sm font-semibold text-ink">{planName}</h3>}
        </div>
        <p className="mt-0.5 text-xs text-ink-muted">
          {tierMode} mode
          {elevatedPeriodMonths != null &&
            ` · ${String(elevatedPeriodMonths)} month elevated window`}
          {currentTier !== null && (
            <span className="ml-2 text-profit font-medium">
              · Current rate: {(Number(currentTier.rate) * 100).toFixed(1)}%
            </span>
          )}
        </p>
      </div>

      {/* Gauge bar */}
      <TierGauge tiers={tiers} currentGp={currentGp} />

      {/* Tier ladder table */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-edge text-left">
              <th className="py-1.5 pr-3 font-medium text-ink-muted">From</th>
              <th className="py-1.5 pr-4 font-medium text-ink-muted">To</th>
              {hasSchedules ? (
                schedules.map((sched, i) => (
                  <th key={i} className="py-1.5 pr-3 font-medium text-ink-muted">
                    <span>{sched.isElevated ? 'Elevated' : 'Standard'}</span>
                    {sched.isElevated && (
                      <span className="ml-1.5 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">
                        ELEVATED
                      </span>
                    )}
                  </th>
                ))
              ) : (
                <th className="py-1.5 pr-3 font-medium text-ink-muted">Rate</th>
              )}
            </tr>
          </thead>
          <tbody>
            {ranges.map((range, rowIdx) => {
              const tier = tiers[rowIdx]!;
              const isActive =
                currentGp >= Number(tier.threshold) &&
                (rowIdx + 1 >= tiers.length || currentGp < Number(tiers[rowIdx + 1]!.threshold));

              return (
                <tr
                  key={tier.threshold}
                  className={`border-b border-edge/50 ${isActive ? 'bg-profit/5' : ''}`}
                >
                  <td className="py-1.5 pr-3 font-mono text-ink">
                    <span className="inline-flex items-center gap-2">
                      {isActive && (
                        <span className="inline-block h-2 w-2 rounded-full bg-profit shrink-0" />
                      )}
                      {range.fromLabel}
                    </span>
                  </td>
                  <td className="py-1.5 pr-4 font-mono text-ink">{range.toLabel}</td>
                  {hasSchedules ? (
                    schedules.map((sched, schedIdx) => {
                      const rate = sched.rates[rowIdx];
                      const pct =
                        rate !== undefined ? `${(Number(rate) * 100).toFixed(1)}%` : '\u2014';
                      return (
                        <td
                          key={schedIdx}
                          className={`py-1.5 pr-3 font-mono ${isActive ? 'text-profit font-semibold' : 'text-ink'}`}
                        >
                          {pct}
                        </td>
                      );
                    })
                  ) : (
                    <td
                      className={`py-1.5 pr-3 font-mono ${isActive ? 'text-profit font-semibold' : 'text-ink'}`}
                    >
                      {(Number(tier.rate) * 100).toFixed(1)}%
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
